// eslint-disable-next-line no-restricted-imports -- Facade internals: this file is part of the @townsquare/router wrapper around RRR. Remove with the insource (VOLTPLAT-330).
import { Route } from 'react-resource-router';

import { Apps } from '@townsquare/app-context';
import { getConfig } from '@townsquare/config';

import type {
	AccessControlCapability,
	CapabilityContainer,
} from './CapabilityRouter/__generated__/CapabilityRouterFragment.graphql';
import type { RouteWithOutlet, RouteWithSubproduct, UserCapabilityType } from './types';

export function isRouteWithOutlet(route: Route): route is RouteWithOutlet {
	return Object.prototype.hasOwnProperty.call(route, 'type');
}

export function isRouteWithSubproduct(route: Route): route is RouteWithSubproduct {
	return Object.prototype.hasOwnProperty.call(route, 'subproduct');
}

export const getRoleFromCapability = (
	capabilityContainer: CapabilityContainer,
	capability: AccessControlCapability,
): UserCapabilityType => {
	return `${capabilityContainer.toLowerCase()}:${capability.toLowerCase()}` as UserCapabilityType;
};

export const ORG_SITE_ROUTE_REGEX: RegExp = new RegExp(
	`^/o/(?<orgIdentifier>[0-9a-zA-Z-]+)(/s/(?<siteIdentifier>[a-zA-Z0-9%+-]+))*`,
);

// Strips the URL to get route path
// eg. `https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals/{extra}` to `/goals/{extra}`
export const getRoutePathFromUrl = (url: string): string => {
	return url.replace(getConfig().fullUrl, '').replace(ORG_SITE_ROUTE_REGEX, '');
};

export const isGoalsAppRoute = (url: string): boolean => {
	const path = getRoutePathFromUrl(url);
	return path.startsWith('/goal') || path.startsWith('/agg-goal') || path === '/action/search/goals';
};

export const isProjectsAppRoute = (url: string): boolean => {
	const path = getRoutePathFromUrl(url);
	return path.startsWith('/project') || path === '/action/search/projects';
};

export const isTeamsAppRoute = (url: string): boolean => {
	const path = getRoutePathFromUrl(url);
	return path.startsWith('/people') || path === '/action/search/teams' || path === '/action/search/people';
};

export const isStudioAppRoute = (url: string): boolean => {
	const path = getRoutePathFromUrl(url);
	return path.startsWith('/studio');
};

/**
 *
 * @param route URL or path of a link ie. https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals` or `/goals`
 * @param app App ie. Goals, Teams, Home
 * @returns if route belongs to app
 */
export const isRouteOfApp = (route: string, app: Apps): boolean => {
	if (isGoalsAppRoute(route)) {
		return app === 'goals';
	}

	if (isProjectsAppRoute(route)) {
		return app === 'projects';
	}

	if (isTeamsAppRoute(route)) {
		return app === 'teams';
	}

	// Legacy studio routes do not belong to any Townsquare app
	if (isStudioAppRoute(route)) {
		return false;
	}

	return app === 'home';
};

/**
 *
 * @param route URL or path of a link ie. https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals` or `/goals`
 * @returns the destination Townsquare app for the given route
 */
export const getDestinationApp = (route: string): 'goals' | 'projects' | 'teams' | 'home' => {
	if (isGoalsAppRoute(route)) {
		return 'goals';
	}
	if (isProjectsAppRoute(route)) {
		return 'projects';
	}
	if (isTeamsAppRoute(route)) {
		return 'teams';
	}
	return 'home';
};

/**
 *
 * @param route URL or path of a link ie. https://home.stg.atlassian.com/o/{orgId}/s/{siteId}/goals` or `/goals`
 * @param app App ie. Goals, Teams, Home
 * @returns if route belongs to an app different from the one provided
 */
export const isRouteOfOtherApp = (route: string, app: Apps): boolean => {
	if (isRouteOfApp(route, app)) {
		return false;
	}

	if (app !== 'goals' && isGoalsAppRoute(route)) {
		return true;
	}

	if (app !== 'projects' && isProjectsAppRoute(route)) {
		return true;
	}

	if (app !== 'teams' && isTeamsAppRoute(route)) {
		return true;
	}

	return false;
};
